'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import {
  ArrowRight,
  Sparkles,
  Check,
  Zap,
  Bot,
  MessageSquare,
  ShieldCheck,
  Star,
  Users,
  Clock,
  Send,
  ExternalLink,
  ChevronRight,
  Activity,
  CheckCircle2,
  TrendingUp,
  Flame,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

// Official channel SVG icons for clean brand presentation
export function WhatsAppOfficialLogo({ className = 'size-9' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="24" fill="#25D366" />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M24 9C15.716 9 9 15.716 9 24C9 26.657 9.691 29.155 10.902 31.328L9.2 38L16.038 36.324C18.146 37.404 20.985 38 24 38C32.284 38 39 31.284 39 24C39 15.716 32.284 9 24 9ZM19.866 16.273C19.539 15.539 19.193 15.524 18.701 15.503C18.489 15.493 18.251 15.493 18.013 15.493C17.775 15.493 17.387 15.582 17.06 15.939C16.733 16.296 15.811 17.159 15.811 18.913C15.811 20.667 17.09 22.362 17.268 22.6C17.447 22.838 19.732 26.464 23.313 27.921C26.15 29.074 26.865 28.843 27.49 28.784C28.412 28.695 29.72 27.981 29.988 27.238C30.256 26.495 30.256 25.84 30.196 25.721C30.137 25.602 29.988 25.513 29.631 25.334C29.274 25.156 27.519 24.294 27.192 24.175C26.865 24.056 26.627 23.996 26.389 24.353C26.151 24.71 25.467 25.513 25.259 25.751C25.051 25.989 24.843 26.018 24.486 25.84C24.129 25.662 22.979 25.285 21.617 24.071C20.559 23.127 19.843 21.96 19.635 21.603C19.427 21.246 19.612 21.047 19.791 20.87C19.953 20.707 20.151 20.446 20.33 20.237C20.508 20.029 20.568 19.88 20.687 19.642C20.806 19.404 20.746 19.196 20.657 19.018C20.568 18.84 19.915 17.24 19.866 16.273Z"
        fill="white"
      />
    </svg>
  );
}

export function InstagramOfficialLogo({ className = 'size-9' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id="ig-hero-gradient" cx="20%" cy="110%" r="130%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="48" height="48" rx="13" fill="url(#ig-hero-gradient)" />
      <rect x="11" y="11" width="26" height="26" rx="7.5" stroke="#ffffff" strokeWidth="3" fill="none" />
      <circle cx="24" cy="24" r="6" stroke="#ffffff" strokeWidth="3" fill="none" />
      <circle cx="31.5" cy="16.5" r="1.8" fill="#ffffff" />
    </svg>
  );
}

export function MessengerOfficialLogo({ className = 'size-9' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="msg-hero-gradient" x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#00C6FF" />
          <stop offset="50%" stopColor="#0078FF" />
          <stop offset="100%" stopColor="#A827FF" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="24" fill="url(#msg-hero-gradient)" />
      <path
        d="M26.132 28.886l-5.44-5.8-10.612 5.8 11.674-12.394 5.568 5.8 10.482-5.8-11.672 12.394z"
        fill="#ffffff"
      />
    </svg>
  );
}

function WhatsAppIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2zm.01 1.67c4.54 0 8.24 3.7 8.24 8.24 0 2.2-.86 4.28-2.42 5.84a8.18 8.18 0 01-5.82 2.41c-1.47 0-2.91-.39-4.17-1.14l-.3-.18-3.12.82.83-3.04-.2-.31a8.216 8.216 0 01-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24zm4.52 11.64c-.25-.13-1.47-.72-1.7-.81-.23-.08-.39-.13-.56.13-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.13-1.06-.39-2.02-1.25-.75-.67-1.25-1.5-1.4-1.75-.14-.25-.02-.39.11-.51.11-.11.25-.29.38-.44.13-.14.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.13-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.03 2.61.13.17 1.77 2.71 4.3 3.8 2.52 1.09 2.52.73 2.98.69.46-.04 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28z" />
    </svg>
  );
}

function InstagramIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
    </svg>
  );
}

function MessengerIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.477 2 2 6.145 2 11.26c0 2.914 1.455 5.525 3.734 7.218V22l3.375-1.852c.915.253 1.884.391 2.891.391 5.523 0 10-4.146 10-9.26C22 6.145 17.523 2 12 2zm1.066 12.443l-2.72-2.9-5.306 2.9 5.837-6.197 2.784 2.9 5.241-2.9-5.836 6.197z" />
    </svg>
  );
}

function TelegramIcon({ className = 'size-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.75-.55 2.93-1.28 4.88-2.12 5.86-2.54 2.79-1.16 3.37-1.36 3.75-1.36.08 0 .28.02.4.12.1.08.13.2.14.28-.01.07.01.23 0 .38z" />
    </svg>
  );
}

interface HeroSectionProps {
  siteName?: string;
  translations?: {
    badge?: string;
    title_part1?: string;
    title_part2?: string;
    subtitle?: string;
    cta_primary?: string;
    cta_secondary?: string;
  };
}

export function HeroSection({ siteName = 'Zeric.link', translations }: HeroSectionProps) {
  const [activeChannel, setActiveChannel] = useState<'whatsapp' | 'instagram' | 'messenger' | 'telegram'>('whatsapp');

  const channels = [
    {
      id: 'whatsapp' as const,
      name: 'WhatsApp Official & Web',
      shortName: 'WhatsApp',
      icon: WhatsAppIcon,
      color: 'emerald',
      activeColor: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30 shadow-emerald-500/10',
      badge: 'Meta Cloud API v21',
      perks: 'Official Green Badge, Catalog Checkout & 99.9% Delivery',
      metric: '< 350ms response',
    },
    {
      id: 'instagram' as const,
      name: 'Instagram DMs & Stories',
      shortName: 'Instagram',
      icon: InstagramIcon,
      color: 'pink',
      activeColor: 'bg-gradient-to-r from-purple-500/15 via-pink-500/15 to-amber-500/15 text-pink-600 dark:text-pink-400 border-pink-500/30 shadow-pink-500/10',
      badge: 'Story Replies + DMs',
      perks: 'Auto Lead Capture from Stories, Comments & Direct Messages',
      metric: 'Zero missed leads',
    },
    {
      id: 'messenger' as const,
      name: 'Facebook Messenger',
      shortName: 'Messenger',
      icon: MessengerIcon,
      color: 'blue',
      activeColor: 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/30 shadow-blue-500/10',
      badge: 'Click-to-Chat Ads',
      perks: 'Automated Lead Qualification from Facebook Pages & Paid Ads',
      metric: 'Instant resolution',
    },
    {
      id: 'telegram' as const,
      name: 'Telegram & Web Chat',
      shortName: 'Web & Telegram',
      icon: TelegramIcon,
      color: 'cyan',
      activeColor: 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-400 border-cyan-500/30 shadow-cyan-500/10',
      badge: 'Embeddable Widget',
      perks: 'Live Website Chat Widget + Telegram Bot in One Unified CRM',
      metric: 'Sub-second sync',
    },
  ];

  const currentChannelInfo = channels.find((c) => c.id === activeChannel) || channels[0];

  return (
    <section className="relative pt-12 pb-20 md:pt-20 md:pb-28 overflow-hidden">
      {/* ATMOSPHERIC BACKGROUND LIGHTING & MESH */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        {/* Top vibrant ambient radial glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[480px] bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.18),rgba(99,102,241,0.12),rgba(236,72,153,0.06),transparent_70%)] dark:bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.22),rgba(99,102,241,0.16),rgba(236,72,153,0.08),transparent_70%)] blur-2xl" />

        {/* Ambient floating glow orbs */}
        <div className="absolute top-24 left-[10%] w-72 h-72 rounded-full bg-emerald-500/10 dark:bg-emerald-500/15 blur-3xl" />
        <div className="absolute top-36 right-[12%] w-80 h-80 rounded-full bg-indigo-500/10 dark:bg-indigo-500/15 blur-3xl" />

        {/* Precision micro-grid texture with smooth radial mask */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800d_1px,transparent_1px),linear-gradient(to_bottom,#8080800d_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_80%_65%_at_50%_15%,#000_65%,transparent_100%)]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        
        {/* TOP PILL / ANNOUNCEMENT BADGE */}
        <div className="inline-flex items-center gap-2.5 mb-7 px-4 py-1.5 rounded-full text-xs font-semibold border border-primary/25 bg-background/80 hover:bg-background/95 backdrop-blur-md text-foreground shadow-sm hover:border-primary/50 transition-all cursor-pointer group animate-in fade-in zoom-in duration-500">
          <span className="relative flex size-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full size-2.5 bg-emerald-500" />
          </span>
          <span className="text-emerald-600 dark:text-emerald-400 font-mono font-bold uppercase tracking-wider text-[11px]">
            Official Meta Cloud Ready
          </span>
          <span className="text-muted-foreground/50">•</span>
          <span className="text-muted-foreground group-hover:text-foreground transition-colors">
            {translations?.badge || '⚡ Autonomous AI + Omnichannel Team CRM'}
          </span>
          <ChevronRight className="size-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </div>

        {/* MAIN HERO HEADLINE */}
        <div className="max-w-5xl mx-auto mb-6">
          <h1 className="text-3xl sm:text-5xl lg:text-[52px] font-black tracking-tight text-foreground leading-[1.2] animate-in fade-in slide-in-from-bottom-4 duration-700">
            <span className="block mb-3 sm:mb-4">
              {translations?.title_part1 || 'Automate Sales & Support with'}
            </span>
            <span className="inline-flex items-center justify-center flex-wrap gap-3 sm:gap-4 md:gap-5 align-middle">
              {/* WhatsApp Official Logo */}
              <span
                className="group relative inline-flex items-center justify-center p-2 sm:p-2.5 rounded-2xl sm:rounded-3xl bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 hover:border-[#25D366]/60 shadow-lg shadow-[#25D366]/15 hover:shadow-[#25D366]/30 transition-all duration-300 hover:scale-110 cursor-pointer"
                title="WhatsApp"
                aria-label="WhatsApp"
              >
                <WhatsAppOfficialLogo className="size-8 sm:size-10 md:size-12 drop-shadow-[0_2px_8px_rgba(37,211,102,0.45)]" />
              </span>

              {/* Instagram Official Logo */}
              <span
                className="group relative inline-flex items-center justify-center p-2 sm:p-2.5 rounded-2xl sm:rounded-3xl bg-gradient-to-tr from-amber-500/10 via-pink-500/10 to-purple-500/10 hover:from-amber-500/20 hover:via-pink-500/20 hover:to-purple-500/20 border border-pink-500/30 hover:border-pink-500/60 shadow-lg shadow-pink-500/15 hover:shadow-pink-500/30 transition-all duration-300 hover:scale-110 cursor-pointer"
                title="Instagram"
                aria-label="Instagram"
              >
                <InstagramOfficialLogo className="size-8 sm:size-10 md:size-12 drop-shadow-[0_2px_8px_rgba(225,48,108,0.45)]" />
              </span>

              {/* Messenger Official Logo */}
              <span
                className="group relative inline-flex items-center justify-center p-2 sm:p-2.5 rounded-2xl sm:rounded-3xl bg-[#0084FF]/10 hover:bg-[#0084FF]/20 border border-[#0084FF]/30 hover:border-[#0084FF]/60 shadow-lg shadow-[#0084FF]/15 hover:shadow-[#0084FF]/30 transition-all duration-300 hover:scale-110 cursor-pointer"
                title="Messenger"
                aria-label="Messenger"
              >
                <MessengerOfficialLogo className="size-8 sm:size-10 md:size-12 drop-shadow-[0_2px_8px_rgba(0,132,255,0.45)]" />
              </span>

              {/* AI Badge */}
              <span
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3.5 sm:px-5 py-1.5 sm:py-2 rounded-2xl sm:rounded-3xl bg-gradient-to-r from-emerald-500/10 via-teal-500/10 to-indigo-500/10 hover:from-emerald-500/20 hover:via-teal-500/20 hover:to-indigo-500/20 border border-teal-500/35 hover:border-teal-500/60 shadow-lg shadow-teal-500/10 hover:shadow-teal-500/25 transition-all duration-300 hover:scale-105 cursor-default align-middle"
              >
                <Sparkles className="size-5 sm:size-6 md:size-7 text-teal-500 animate-pulse" />
                <span className="text-2xl sm:text-4xl md:text-5xl font-black bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 dark:from-emerald-400 dark:via-teal-400 dark:to-indigo-400 bg-clip-text text-transparent">
                  AI
                </span>
              </span>
            </span>
          </h1>
        </div>

        {/* HERO SUBTITLE */}
        <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto leading-relaxed font-normal animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
          Deploy <span className="text-foreground font-semibold">24/7 autonomous AI agents</span> grounded in your company knowledge, build <span className="text-foreground font-semibold">visual flow automations</span>, and close deals effortlessly in <span className="text-foreground font-semibold">one unified team CRM inbox</span>.
        </p>

        {/* HIGH-CONVERTING CTA BUTTONS */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3.5 mb-4 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
          <Link href="/sign-up" className="w-full sm:w-auto">
            <Button
              size="lg"
              className="w-full sm:w-auto rounded-xl px-8 h-12 text-sm sm:text-base font-bold shadow-xl shadow-emerald-500/20 hover:shadow-emerald-500/30 transition-all bg-emerald-600 hover:bg-emerald-500 text-white gap-2 cursor-pointer"
            >
              <span>{translations?.cta_primary || 'Start Free 7-Day Trial'}</span>
              <ArrowRight className="size-4" />
            </Button>
          </Link>

          <a href="#interactive-demo" className="w-full sm:w-auto">
            <Button
              variant="outline"
              size="lg"
              className="w-full sm:w-auto rounded-xl px-7 h-12 text-sm sm:text-base font-semibold border-border/80 hover:bg-muted/70 transition-all gap-2 cursor-pointer backdrop-blur-sm"
            >
              <Sparkles className="size-4 text-emerald-500" />
              <span>{translations?.cta_secondary || 'Try Live Demo ↓'}</span>
            </Button>
          </a>
        </div>

        {/* SUB-CTA TRUST GUARANTEES */}
        <div className="flex flex-wrap items-center justify-center gap-y-2 gap-x-6 text-xs text-muted-foreground mb-8 font-medium animate-in fade-in duration-700 delay-250">
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-emerald-500" /> No credit card required
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-emerald-500" /> 60-second setup
          </span>
          <span className="flex items-center gap-1.5">
            <Check className="size-3.5 text-emerald-500" /> Cancel anytime
          </span>
        </div>

        {/* SOCIAL PROOF & CUSTOMER AVATAR STACK */}
        <div className="flex flex-wrap items-center justify-center gap-3 mb-12 animate-in fade-in duration-700 delay-300">
          <div className="flex -space-x-2">
            <img
              src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=64&h=64&fit=crop&crop=face"
              alt="Customer Avatar"
              className="size-8 rounded-full border-2 border-background object-cover shadow-xs"
            />
            <img
              src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face"
              alt="Customer Avatar"
              className="size-8 rounded-full border-2 border-background object-cover shadow-xs"
            />
            <img
              src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=face"
              alt="Customer Avatar"
              className="size-8 rounded-full border-2 border-background object-cover shadow-xs"
            />
            <img
              src="https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=64&h=64&fit=crop&crop=face"
              alt="Customer Avatar"
              className="size-8 rounded-full border-2 border-background object-cover shadow-xs"
            />
          </div>
          <div className="flex items-center gap-1 text-amber-500">
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="size-3.5 fill-current" />
            ))}
          </div>
          <p className="text-xs text-muted-foreground font-medium">
            <strong className="text-foreground font-semibold">4.9/5</strong> rating from{' '}
            <strong className="text-foreground font-semibold">2,400+</strong> businesses worldwide
          </p>
        </div>

        {/* INTERACTIVE CONNECTED CHANNELS ECOSYSTEM BAR */}
        <div className="max-w-4xl mx-auto mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700 delay-350">
          <div className="p-3.5 rounded-2xl bg-card/75 border border-border/80 backdrop-blur-md shadow-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pb-3 border-b border-border/60">
              <div className="flex items-center gap-2 text-xs font-semibold text-foreground">
                <Activity className="size-4 text-emerald-500" />
                <span className="font-mono uppercase tracking-wider text-[11px] text-muted-foreground">
                  Connected Channels:
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-medium">
                <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                <span>All Gateways Operational (Meta Graph API v21.0)</span>
              </div>
            </div>

            {/* Channel Tabs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-3">
              {channels.map((ch) => {
                const isSelected = activeChannel === ch.id;
                const IconComponent = ch.icon;

                return (
                  <button
                    key={ch.id}
                    type="button"
                    onClick={() => setActiveChannel(ch.id)}
                    className={`flex items-center gap-2.5 p-2.5 rounded-xl border transition-all text-left cursor-pointer ${
                      isSelected
                        ? `${ch.activeColor} border-current shadow-sm scale-[1.02]`
                        : 'border-border/60 bg-muted/30 hover:bg-muted/60 text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <div className="size-7 rounded-lg bg-background/80 flex items-center justify-center shrink-0 shadow-2xs">
                      <IconComponent className="size-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-bold truncate">{ch.shortName}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{ch.badge}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Active Channel Superpower Highlight Bar */}
            <div className="mt-3 px-3 py-2 rounded-xl bg-muted/40 border border-border/50 flex flex-col sm:flex-row items-center justify-between gap-2 text-left">
              <div className="flex items-center gap-2 text-xs text-foreground">
                <span className="size-1.5 rounded-full bg-primary shrink-0" />
                <span className="font-medium text-muted-foreground">Capabilities:</span>
                <span className="font-semibold">{currentChannelInfo.perks}</span>
              </div>
              <span className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400 font-bold shrink-0 bg-emerald-500/10 px-2 py-0.5 rounded-md">
                {currentChannelInfo.metric}
              </span>
            </div>
          </div>
        </div>

        {/* 3 SIMPLE VALUE PILLARS / HOW IT WORKS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 text-left max-w-5xl mx-auto">
          {/* Pillar 1 */}
          <div className="p-6 rounded-2xl border border-border/70 bg-card/60 backdrop-blur-sm hover:border-emerald-500/40 hover:shadow-xl transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="size-11 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-110 transition-transform shadow-xs">
                <MessageSquare className="size-5" />
              </div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-muted-foreground bg-muted/80 px-2.5 py-0.5 rounded-full border border-border/60">
                01. UNIFY
              </span>
            </div>
            <h3 className="text-base font-bold text-foreground mb-1.5 flex items-center gap-1.5">
              One Unified Team Inbox
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Connect WhatsApp, Instagram DMs, and Facebook Messenger into one shared inbox. Stop switching tabs and never let a prospective lead slip through the cracks.
            </p>
          </div>

          {/* Pillar 2 */}
          <div className="p-6 rounded-2xl border border-border/70 bg-card/60 backdrop-blur-sm hover:border-purple-500/40 hover:shadow-xl transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="size-11 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-600 dark:text-purple-400 group-hover:scale-110 transition-transform shadow-xs">
                <Bot className="size-5" />
              </div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-muted-foreground bg-muted/80 px-2.5 py-0.5 rounded-full border border-border/60">
                02. AUTOMATE
              </span>
            </div>
            <h3 className="text-base font-bold text-foreground mb-1.5 flex items-center gap-1.5">
              Grounded 24/7 AI Agents
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Trained on your business docs, PDFs &amp; product catalog. Responds in &lt; 500ms with accurate, polite answers and zero hallucinations around the clock.
            </p>
          </div>

          {/* Pillar 3 */}
          <div className="p-6 rounded-2xl border border-border/70 bg-card/60 backdrop-blur-sm hover:border-blue-500/40 hover:shadow-xl transition-all group">
            <div className="flex items-center justify-between mb-4">
              <div className="size-11 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform shadow-xs">
                <Zap className="size-5" />
              </div>
              <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-muted-foreground bg-muted/80 px-2.5 py-0.5 rounded-full border border-border/60">
                03. CONVERT
              </span>
            </div>
            <h3 className="text-base font-bold text-foreground mb-1.5 flex items-center gap-1.5">
              Visual Flows &amp; Close Deals
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
              Build drag-and-drop flows, send interactive product menus with 1-click checkout links, and smoothly hand off high-tier conversations to human agents.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
